import { formatDate } from "@memorang/helpers/date";
import { useEffect, useRef, useState } from "react";
import { type LayoutChangeEvent, Pressable, StyleSheet } from "react-native";
import { Chip, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import AppbarAction from "../AppbarAction";
import Box from "../Box";
import { ScrollButtonFlatlist } from "../ChatUI/ScrollButtonFlatlist";
import CitationCarousel from "../CitationCard/CitationCarousel";
import { CustomAppBar } from "../CustomAppbar";
import Image from "../CustomImage";
import Menu from "../Menu";
import { PodcastPlayerSheet } from "../PodcastPlayerSheet";
import ScrollContainer from "../ScrollContainer";
import ActionRow from "./ActionRow";
import type { MenuAction, PodcastDetailAppletProps } from "./types";

const OVERFLOW_MENU_ITEMS: { title: MenuAction; leadingIcon: string }[] = [
	{ title: "Share", leadingIcon: "share-variant-outline" },
	{ title: "Report", leadingIcon: "flag-outline" },
];

export type { PodcastDetailAppletProps };
export type {
	MenuAction,
	NowPlayingProps,
	PodcastCitation,
	PodcastEpisode,
} from "./types";

export const PodcastDetailApplet = ({
	episode,
	isBookmarked,
	isInQueue,
	onBackPress,
	onToggleBookmark,
	onShare,
	onToggleQueue,
	onPlayPress,
	onPressCitation,
	onMenuItemPress,
	nowPlayingProps,
}: PodcastDetailAppletProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const [menuVisible, setMenuVisible] = useState(false);
	const [showNowPlaying, setShowNowPlaying] = useState(false);
	const [descriptionExpanded, setDescriptionExpanded] = useState(false);
	const [descriptionTruncated, setDescriptionTruncated] = useState(false);
	const fullHeightRef = useRef(0);
	const clampedHeightRef = useRef(0);

	// biome-ignore lint/correctness/useExhaustiveDependencies: reset description state when navigating to a different episode
	useEffect(() => {
		setDescriptionExpanded(false);
		setDescriptionTruncated(false);
		fullHeightRef.current = 0;
		clampedHeightRef.current = 0;
	}, [episode.id]);

	const checkTruncation = () => {
		if (
			!descriptionTruncated &&
			fullHeightRef.current > 0 &&
			clampedHeightRef.current > 0
		) {
			setDescriptionTruncated(
				fullHeightRef.current > clampedHeightRef.current + 1,
			);
		}
	};

	const onFullLayout = (e: LayoutChangeEvent) => {
		fullHeightRef.current = e.nativeEvent.layout.height;
		checkTruncation();
	};

	const onClampedLayout = (e: LayoutChangeEvent) => {
		if (!descriptionExpanded) {
			clampedHeightRef.current = e.nativeEvent.layout.height;
			checkTruncation();
		}
	};

	const handlePlayPress = () => {
		if (nowPlayingProps) {
			setShowNowPlaying(true);
			if (nowPlayingProps.isPlaying) {
				return;
			}
		}
		onPlayPress?.();
	};

	return (
		<Box flex={1}>
			<CustomAppBar
				title="Episode details"
				renderRight={() => (
					<Menu
						visible={menuVisible}
						onDismiss={() => setMenuVisible(false)}
						anchorPosition="bottom"
						anchor={
							<AppbarAction
								icon="dots-vertical"
								onPress={() => setMenuVisible(true)}
							/>
						}
						items={OVERFLOW_MENU_ITEMS}
						onItemPress={(item) => {
							setMenuVisible(false);
							onMenuItemPress?.(item.title as MenuAction);
						}}
					>
						{null}
					</Menu>
				)}
				onPressBack={onBackPress}
			/>

			<ScrollContainer maxWidth="sm">
				<Box
					paddingTop={16}
					paddingHorizontal={16}
					gap={8}
					alignItems="flex-start"
				>
					<Box
						gap={16}
						alignItems={isMobile ? "center" : "flex-start"}
						width="100%"
					>
						<Image
							source={{ uri: episode.artworkUri }}
							accessibilityLabel={`${episode.title} artwork`}
							style={styles.artwork}
							contentFit="cover"
						/>
						<Box gap={4} width="100%">
							<Text
								variant="titleMedium"
								style={{ color: theme.colors.onSurface }}
							>
								{episode.title}
							</Text>
							<Text
								variant="labelMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								{episode.author}
							</Text>
							<Box paddingTop={4}>
								<Text
									variant="bodySmall"
									style={{ color: theme.colors.onSurfaceVariant }}
								>
									{formatDate(episode.date, "en-GB", {
										day: "numeric",
										month: "short",
										year: "numeric",
										timeZone: "UTC",
									}).toUpperCase()}
								</Text>
							</Box>
						</Box>
					</Box>
					<ActionRow
						durationSeconds={episode.durationSeconds}
						isPlaying={!!nowPlayingProps?.isPlaying}
						isBookmarked={isBookmarked}
						isInQueue={isInQueue}
						onPlayPress={handlePlayPress}
						onToggleQueue={onToggleQueue}
						onToggleBookmark={onToggleBookmark}
						onShare={onShare}
					/>
				</Box>

				<Box padding={16} gap={16} flex={1}>
					<Pressable
						onPress={() => setDescriptionExpanded((prev) => !prev)}
						disabled={!descriptionTruncated}
						accessibilityRole="button"
						accessibilityLabel={
							descriptionExpanded
								? "Collapse description"
								: "Expand description"
						}
					>
						<Text
							variant="bodyMedium"
							onLayout={onFullLayout}
							style={styles.measureText}
						>
							{episode.description}
						</Text>
						<Text
							variant="bodyMedium"
							numberOfLines={descriptionExpanded ? undefined : 3}
							onLayout={onClampedLayout}
							style={{ color: theme.colors.onSurfaceVariant }}
						>
							{episode.description}
						</Text>
						{descriptionTruncated && (
							<Text
								variant="labelMedium"
								style={[styles.showMoreLabel, { color: theme.colors.primary }]}
							>
								{descriptionExpanded ? "Show less" : "Show more"}
							</Text>
						)}
					</Pressable>

					{episode.tags.length > 0 && (
						<Box gap={8}>
							<Text
								variant="labelMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								Tags
							</Text>
							<ScrollButtonFlatlist
								data={episode.tags}
								horizontal
								showsHorizontalScrollIndicator={false}
								keyExtractor={(tag) => tag}
								contentContainerStyle={styles.tagsContentContainer}
								renderItem={({ item: tag }) => (
									<Chip
										style={{
											backgroundColor: theme.colors.secondaryContainer,
											borderRadius: 8,
										}}
										textStyle={{
											color: theme.colors.onSecondaryContainer,
										}}
									>
										{tag}
									</Chip>
								)}
							/>
						</Box>
					)}

					{episode.citations.length > 0 && (
						<Box gap={8}>
							<Text
								variant="labelMedium"
								style={{ color: theme.colors.onSurfaceVariant }}
							>
								Citations
							</Text>
							<CitationCarousel
								citations={episode.citations}
								onPressCitation={onPressCitation}
							/>
						</Box>
					)}

					{!isMobile && showNowPlaying && <Box height={96} />}
				</Box>
			</ScrollContainer>

			{nowPlayingProps && (
				<PodcastPlayerSheet
					{...nowPlayingProps}
					isBookmarked={isBookmarked}
					onToggleBookmark={onToggleBookmark}
					isInPlaylist={isInQueue}
					onTogglePlaylist={onToggleQueue}
					showModal={showNowPlaying}
					onDismiss={() => setShowNowPlaying(false)}
				/>
			)}
		</Box>
	);
};

const styles = StyleSheet.create({
	artwork: {
		width: 144,
		height: 144,
		borderRadius: 12,
	},
	measureText: {
		position: "absolute",
		opacity: 0,
		left: 0,
		right: 0,
		pointerEvents: "none",
	},
	showMoreLabel: {
		marginTop: 4,
	},
	tagsContentContainer: {
		gap: 8,
	},
});
